import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { documentService } from '../services/documentService';
import { reminderService } from '../services/reminderService';
import { logger } from '../utils/logger';
import { userErrorMessage } from '../utils/userError';

/**
 * Background upload manager.
 *
 * Exposes `enqueueFile`, `enqueueFiles` (array), and `enqueueFolder`. Each
 * enqueue starts the upload right away and returns immediately — the caller
 * (the DocumentUpload modal, drag-drop handlers, etc.) is never blocked.
 *
 * Tracked state per upload:
 *   { id, type, name, parentFolderId, status, progress, total, current,
 *     currentFile?, error?, successCount?, failureCount?, failures?,
 *     extractedCount?, startedAt, finishedAt? }
 *
 * Status lifecycle:
 *   pending → creating (folders only) → uploading | processing →
 *     completed | partial | failed
 *
 * Each completion fires an `omnis:document-upload-complete` window event so
 * file-list views (FileManager, DocumentsPanel previews if any) can refresh.
 *
 * Notifications stay on screen until dismissed manually — this is the
 * "user clears it" behavior the product wants.
 */

const UploadManagerContext = createContext(null);

const newId = () => `upload-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const isArchive = (name) => /\.(zip|7z)$/i.test(name || '');

const fireCompleteEvent = (detail = {}) => {
    try {
        window.dispatchEvent(new CustomEvent('omnis:document-upload-complete', { detail }));
    } catch (_) { /* ignore */ }
};

export const UploadManagerProvider = ({ children }) => {
    const [uploads, setUploads] = useState([]);
    // Track which uploads we've already fired a "Document uploaded" reminder
    // for. Lives outside React state so re-renders don't reset it.
    const [reminderFiredIds, setReminderFiredIds] = useState(() => new Set());

    const updateUpload = useCallback((id, patch) => {
        setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, ...(typeof patch === 'function' ? patch(u) : patch) } : u)));
    }, []);

    const addUpload = useCallback((upload) => {
        setUploads((prev) => [...prev, upload]);
    }, []);

    const dismiss = useCallback((id) => {
        setUploads((prev) => prev.filter((u) => u.id !== id));
    }, []);

    const clearFinished = useCallback(() => {
        setUploads((prev) => prev.filter((u) => !['completed', 'failed', 'partial'].includes(u.status)));
    }, []);

    /** Run a single-file upload to completion, updating its tracker. */
    const runFileUpload = useCallback(async (id, file, parentFolderId) => {
        const archive = isArchive(file.name);

        try {
            const handleProgress = (event) => {
                if (event?.type === 'start') {
                    updateUpload(id, {
                        status: 'processing',
                        total: event.totalFiles || 0,
                        current: 0,
                        progress: 0,
                    });
                } else if (event?.type === 'progress') {
                    const total = event.total || 0;
                    const current = event.current || 0;
                    const pct = total > 0 ? Math.round((current / total) * 100) : 0;
                    updateUpload(id, {
                        status: 'processing',
                        total,
                        current,
                        progress: pct,
                        currentFile: event.filename || null,
                    });
                }
            };

            // Real byte-level progress while the file body uploads. Once the
            // server responds the status flips to 'processing' (archive) or
            // 'completed' (single file).
            const handleBytes = ({ percent }) => {
                updateUpload(id, (u) => (
                    u.status === 'uploading' ? { progress: Math.min(99, percent) } : u
                ));
            };

            const result = parentFolderId
                ? await documentService.uploadDocumentToFolder(file, parentFolderId, archive ? handleProgress : null, handleBytes)
                : await documentService.uploadDocument(file, archive ? handleProgress : null, handleBytes);

            // Pull the document id out so the post-upload reminder can deep-
            // link to it. Single uploads → result.document.id; archive
            // extractions → result.documents[*].id (we just take the first one
            // for the reminder link).
            const singleDocId = result?.document?.id || result?.document_id || null;
            const extracted = (result?.documents || []).filter(Boolean);

            const failedMembers = result?.failed || [];
            if (result?.type === 'archive' && failedMembers.length) {
                // #494: the archive imported, but some members did not — say which
                // and why instead of hiding them behind a green tick.
                updateUpload(id, {
                    status: 'partial',
                    progress: 100,
                    total: extracted.length + failedMembers.length,
                    current: extracted.length + failedMembers.length,
                    successCount: extracted.length,
                    failureCount: failedMembers.length,
                    error: failedMembers.map((f) => `${f.filename}: ${userErrorMessage(f.error)}`).join(' · '),
                    documentId: extracted[0]?.id || null,
                    finishedAt: Date.now(),
                });
            } else if (result?.type === 'archive') {
                updateUpload(id, {
                    status: 'completed',
                    progress: 100,
                    total: extracted.length,
                    current: extracted.length,
                    extractedCount: extracted.length,
                    documentId: extracted[0]?.id || null,
                    finishedAt: Date.now(),
                });
            } else {
                updateUpload(id, {
                    status: 'completed',
                    progress: 100,
                    total: 1,
                    current: 1,
                    documentId: singleDocId,
                    finishedAt: Date.now(),
                });
            }
            fireCompleteEvent({ uploadId: id });
        } catch (err) {
            // Filename collision (Global Duplicate Filename Prevention): flag it
            // distinctly so the header indicator can offer a rename-and-retry,
            // keeping the file around for the retry.
            // An archive's members are auto-renamed server-side (#494); a member
            // 409 must never offer to rename the ARCHIVE itself.
            if (!archive && (err?.status === 409 || err?.code === 'duplicate_filename')) {
                updateUpload(id, {
                    status: 'duplicate',
                    error: err?.detail || 'A document with this name already exists. Please provide a unique name.',
                    file,
                    parentFolderId,
                    finishedAt: Date.now(),
                });
                return;
            }
            logger.error('UPLOAD', `Upload failed for ${file.name}:`, err);
            updateUpload(id, {
                status: 'failed',
                error: userErrorMessage(err),
                finishedAt: Date.now(),
            });
        }
    }, [updateUpload]);

    const enqueueFile = useCallback((file, parentFolderId = null) => {
        const id = newId();
        addUpload({
            id,
            type: 'file',
            name: file.name,
            parentFolderId,
            status: 'uploading',
            progress: 0,
            total: 1,
            current: 0,
            startedAt: Date.now(),
        });
        // Run async — never blocks the caller
        Promise.resolve().then(() => runFileUpload(id, file, parentFolderId));
        return id;
    }, [addUpload, runFileUpload]);

    // Retry a duplicate-blocked upload under a new name (rename-before-save flow
    // for the file-manager / background uploads).
    const renameDuplicate = useCallback((id, newName) => {
        const item = uploads.find((u) => u.id === id);
        if (!item || !item.file) return;
        const renamed = new File([item.file], newName, { type: item.file.type });
        updateUpload(id, { status: 'uploading', name: newName, error: null, progress: 0, file: undefined });
        Promise.resolve().then(() => runFileUpload(id, renamed, item.parentFolderId));
    }, [uploads, updateUpload, runFileUpload]);

    /**
     * Enqueue an array of files. Each starts in parallel — the browser/server
     * handles concurrency. For large batches the browser caps in-flight
     * fetches anyway, and the backend handles ordering/locking.
     */
    const enqueueFiles = useCallback((files, parentFolderId = null) => {
        const ids = [];
        for (const file of files) {
            ids.push(enqueueFile(file, parentFolderId));
        }
        return ids;
    }, [enqueueFile]);

    /**
     * Enqueue a folder upload — creates the root folder, walks files into
     * subfolders, uploads each. Continues on per-file errors and reports the
     * tally at the end (`partial` if any failed but not all).
     */
    const enqueueFolder = useCallback((folder, parentFolderId = null) => {
        const id = newId();
        addUpload({
            id,
            type: 'folder',
            name: folder.name,
            parentFolderId,
            status: 'creating',
            progress: 0,
            total: folder.files.length,
            current: 0,
            successCount: 0,
            failureCount: 0,
            failures: [],
            startedAt: Date.now(),
        });

        Promise.resolve().then(async () => {
            try {
                const rootFolder = await documentService.createFolder(folder.name, parentFolderId);
                const rootFolderId = rootFolder.id;
                const folderCache = new Map();

                updateUpload(id, { status: 'uploading' });

                const getOrCreatePath = async (pathParts) => {
                    let cur = rootFolderId;
                    for (const folderName of pathParts) {
                        const cacheKey = `${cur || 'root'}/${folderName}`;
                        if (folderCache.has(cacheKey)) {
                            cur = folderCache.get(cacheKey);
                        } else {
                            const created = await documentService.createFolder(folderName, cur);
                            folderCache.set(cacheKey, created.id);
                            cur = created.id;
                        }
                    }
                    return cur;
                };

                let successCount = 0;
                let failureCount = 0;
                const failures = [];

                for (let j = 0; j < folder.files.length; j++) {
                    const file = folder.files[j];
                    const filePath = (folder.filePaths && folder.filePaths[j]) || file.name;

                    updateUpload(id, {
                        progress: Math.round((j / folder.files.length) * 100),
                        current: j,
                        currentFile: filePath,
                    });

                    try {
                        const pathParts = filePath.split('/');
                        pathParts.shift(); // drop the root folder name (already created)
                        pathParts.pop();   // drop the filename
                        let target = rootFolderId;
                        if (pathParts.length > 0) {
                            target = await getOrCreatePath(pathParts);
                        }
                        await documentService.uploadDocumentToFolder(file, target, null);
                        successCount += 1;
                    } catch (err) {
                        failureCount += 1;
                        failures.push({ filename: filePath, error: userErrorMessage(err) });
                    }
                }

                const finalStatus = failureCount === 0
                    ? 'completed'
                    : (successCount === 0 ? 'failed' : 'partial');

                updateUpload(id, {
                    status: finalStatus,
                    progress: 100,
                    current: folder.files.length,
                    currentFile: null,
                    successCount,
                    failureCount,
                    failures,
                    finishedAt: Date.now(),
                });
                fireCompleteEvent({ uploadId: id });
            } catch (err) {
                logger.error('UPLOAD', `Folder upload failed for ${folder.name}:`, err);
                updateUpload(id, {
                    status: 'failed',
                    error: userErrorMessage(err),
                    finishedAt: Date.now(),
                });
            }
        });

        return id;
    }, [addUpload, updateUpload]);

    // Persistent "Document X has been uploaded." reminders. Fires once per
    // completed upload so the user can find it later in the bell tray; the
    // existing RemindersDropdown handles dismissal + the deep-link click.
    useEffect(() => {
        const newCompletions = uploads.filter(
            (u) => u.status === 'completed' && !reminderFiredIds.has(u.id),
        );
        if (newCompletions.length === 0) return;

        const updates = new Set(reminderFiredIds);
        for (const upload of newCompletions) {
            updates.add(upload.id);
            const isFolder = upload.type === 'folder';
            const isArchive = upload.extractedCount !== undefined;
            let title;
            if (isFolder) {
                title = `Folder “${upload.name}” has been uploaded (${upload.successCount ?? upload.current ?? 0} files).`;
            } else if (isArchive) {
                title = `Archive “${upload.name}” has been uploaded (${upload.extractedCount} files).`;
            } else {
                title = `Document “${upload.name}” has been uploaded.`;
            }
            const link = upload.documentId
                ? `/?focus=document&id=${encodeURIComponent(upload.documentId)}`
                : null;
            (async () => {
                try {
                    // Notification Bug (#346): "Document X has been uploaded"
                    // is a SYSTEM notice PAI generated automatically — the
                    // user never asked to be reminded about it — so it is
                    // minted as `kind: 'system'`, not a genuine reminder.
                    //
                    // Notification Center misalignment (#379): it also declares
                    // its Notification Center type, so Vault → "Processing
                    // Completed" actually governs it — both whether the Bell
                    // shows it and whether it reaches Telegram. That
                    // toggle was one of the PO's three named dead switches:
                    // #346 correctly reclassified this notice as a system one,
                    // but a raw Bell row consults no preferences at all, so the
                    // user's explicit choice had nothing to act on.
                    await reminderService.createReminder({
                        title,
                        remind_at: new Date().toISOString(),
                        link,
                        kind: 'system',
                        notif_type: 'vault.processing_completed',
                    });
                    try {
                        window.dispatchEvent(new CustomEvent('omnis:reminder-created', {
                            detail: { source: 'upload', upload_id: upload.id },
                        }));
                    } catch (_) { /* ignore */ }
                } catch (err) {
                    logger.warn('UPLOAD', 'Failed to record upload reminder:', err);
                }
            })();
        }
        setReminderFiredIds(updates);
    }, [uploads, reminderFiredIds]);

    const value = {
        uploads,
        enqueueFile,
        enqueueFiles,
        enqueueFolder,
        renameDuplicate,
        dismiss,
        clearFinished,
    };

    return (
        <UploadManagerContext.Provider value={value}>
            {children}
        </UploadManagerContext.Provider>
    );
};

export const useUploadManager = () => {
    const ctx = useContext(UploadManagerContext);
    if (!ctx) {
        throw new Error('useUploadManager must be used within UploadManagerProvider');
    }
    return ctx;
};
